/**
 * Studievaardigheden (STU) Support Registry
 *
 * Support content (hints, explanations, visuals) for STU families.
 *
 * Module: STU (Studievaardigheden)
 * Families in taxonomy: 20
 * Families with support: 20 (100% coverage)
 *
 * Created: 2026-03-25
 * Status: ✅ Content migrated from familySupportRegistry.ts (Step 4)
 */

import { FamilySupportEntry } from '../familySupportRegistry'

/**
 * STU Support Definitions
 *
 * Each entry provides:
 * - defaultExplanation: Student-facing "Zo werkt het" text
 * - hints: Progressive hint steps (title + content)
 * - requiresHints: Whether hints are conceptually needed
 * - requiresVisual: Whether visual aid is conceptually needed
 * - defaultVisual: Optional default visual aid reference
 */
export const STU_SUPPORT: Record<string, FamilySupportEntry> = {
  'STU-ALFABET': {
    familyId: 'STU-ALFABET',
    defaultExplanation: 'Alfabetiseren: sorteer op de eerste letter.',
    requiresHints: false,
    requiresVisual: false,
    hints: [
      {
        title: 'Stappenplan',
        content:
          'Stap 1: Kijk naar de eerste letter.\nStap 2: Zijn de eerste letters gelijk? Kijk naar de tweede letter.\nStap 3: Ga zo door tot je een verschil vindt.',
      },
    ],
  },
  'STU-TABEL': {
    familyId: 'STU-TABEL',
    defaultExplanation: 'Bij tabellen: lees eerst de kolomkoppen en rijlabels.',
    requiresHints: true,
    requiresVisual: true,
    defaultVisual: {
      type: 'ratio',
      data: {
        headers: ['Kolom 1', 'Kolom 2'],
        rows: [
          ['Rij 1', 'Data'],
          ['Rij 2', 'Data'],
        ],
      },
      showWhen: 'always',
    },
    hints: [
      {
        title: 'Stappenplan: tabel aflezen',
        content:
          'Stap 1: Lees de kolomkoppen bovenaan\nStap 2: Zoek de juiste rij (links)\nStap 3: Volg de rij naar de juiste kolom\nStap 4: Lees het kruispunt af',
      },
      {
        title: 'Veelgemaakte fouten',
        content:
          'Pas op voor:\n• Verkeerde kolom: "Aantal jongens" vs "Aantal meisjes" — lees de kop!\n• Verkeerde rij: controleer het label links\n• Totaal vs detail: "hoeveel in totaal?" = alle rijen optellen',
      },
      {
        title: 'Vergelijken in een tabel',
        content:
          'Bij "welke is het meest/minst?":\n1. Lees alle waarden in de gevraagde kolom af\n2. Vergelijk de getallen\n3. Zoek de hoogste of laagste waarde\n4. Lees de bijbehorende rij af',
      },
    ],
  },
  'STU-GRAFIEK': {
    familyId: 'STU-GRAFIEK',
    defaultExplanation: 'Bij grafieken: lees eerst de titel en aslabels.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        content:
          'Staafdiagram: vergelijk hoeveelheden\nLijndiagram: veranderingen over tijd\nCirkeldiagram: verdeling (altijd 100%)',
      },
    ],
  },
  'STU-KAART': {
    familyId: 'STU-KAART',
    defaultExplanation: 'Bij kaartlezen: gebruik de legenda.',
    requiresHints: false,
    requiresVisual: true,
    defaultVisual: {
      type: 'geo',
      data: { width: 10, height: 10, unit: 'km', showGrid: true },
      showWhen: 'always',
    },
    hints: [
      {
        title: 'Kaartlezen',
        content:
          'Stap 1: Lees de legenda.\nStap 2: Bekijk de schaal.\nStap 3: Gebruik het kompas (N = noord = boven).',
      },
    ],
  },
  'STU-COMBINEER': {
    familyId: 'STU-COMBINEER',
    defaultExplanation:
      'Informatie combineren: verzamel gegevens uit meerdere bronnen.',
    requiresHints: false,
    requiresVisual: false,
    hints: [
      {
        title: 'Informatie combineren',
        content:
          'Stap 1: Lees elke bron apart.\nStap 2: Zoek gegevens die bij elkaar horen.\nStap 3: Trek een conclusie.',
      },
    ],
  },
  'STU-INHOUDSOPGAVE': {
    familyId: 'STU-INHOUDSOPGAVE',
    defaultExplanation:
      'Een inhoudsopgave laat zien welke onderwerpen in een boek staan en op welke pagina.',
    requiresHints: false,
    requiresVisual: false,
    hints: [
      {
        title: 'Stappenplan',
        content:
          'Stap 1: Zoek het onderwerp in de inhoudsopgave\nStap 2: Lees het paginanummer\nStap 3: Sla het boek open op die pagina',
      },
      {
        title: 'Hoofdstukken vs paragrafen',
        content:
          'Hoofdstukken: grote onderwerpen (1, 2, 3...)\nParagrafen: deelonderwerpen (1.1, 1.2, 1.3...)\nHoe meer cijfers, hoe specifieker het onderwerp.',
      },
    ],
  },
  'STU-WOORDENBOEK': {
    familyId: 'STU-WOORDENBOEK',
    defaultExplanation:
      'In een woordenboek staan woorden op alfabetische volgorde. Je vindt er de betekenis, spelling en woordsoort.',
    requiresHints: false,
    requiresVisual: false,
    hints: [
      {
        title: 'Zoeken in een woordenboek',
        content:
          'Stap 1: Zoek op de eerste letter\nStap 2: Dan de tweede letter, enzovoort\nStap 3: Gebruik de kopwoorden bovenaan de pagina',
      },
      {
        title: 'Afkortingen',
        content:
          'znw = zelfstandig naamwoord\nww = werkwoord\nbnw = bijvoeglijk naamwoord\nbw = bijwoord\nmv = meervoud',
      },
    ],
  },
  'STU-INTERNET': {
    familyId: 'STU-INTERNET',
    defaultExplanation:
      'Bij zoeken op internet gebruik je zoekwoorden. Kies specifieke woorden en controleer of de bron betrouwbaar is.',
    requiresHints: false,
    requiresVisual: false,
    hints: [
      {
        title: 'Goede zoekwoorden',
        content:
          'Gebruik specifieke woorden:\n❌ "dieren" (te breed)\n✅ "leefgebied pinguïn Antarctica"\n\nGebruik aanhalingstekens voor exacte zinnen:\n"hoofdstad van Frankrijk"',
      },
      {
        title: 'Betrouwbaarheid',
        content:
          'Controleer altijd:\n• Wie heeft het geschreven?\n• Is het recent?\n• Staat het op meerdere sites?\n• Is het een .nl, .org of .edu site?',
      },
    ],
  },
  'STU-BIBLIOTHEEK': {
    familyId: 'STU-BIBLIOTHEEK',
    defaultExplanation:
      'In de bibliotheek zijn boeken geordend op onderwerp. Gebruik de catalogus om boeken te vinden.',
    requiresHints: false,
    requiresVisual: false,
    hints: [
      {
        title: 'Boeken vinden',
        content:
          'Stap 1: Zoek in de catalogus (computer of kaartenbak)\nStap 2: Noteer het nummer/code\nStap 3: Zoek de juiste kast\nStap 4: Zoek op het rug-etiket',
      },
    ],
  },
  'STU-DIAGRAM': {
    familyId: 'STU-DIAGRAM',
    defaultExplanation:
      'Diagrammen tonen informatie visueel. Lees altijd eerst de titel, legenda en aslabels.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Stappenplan',
        content:
          'Stap 1: Lees de titel (waar gaat het over?)\nStap 2: Lees de aslabels (wat staat er op de assen?)\nStap 3: Lees de legenda (wat betekenen de kleuren?)\nStap 4: Lees de waarden af',
      },
    ],
  },
  'STU-STAAF': {
    familyId: 'STU-STAAF',
    defaultExplanation:
      'Een staafdiagram vergelijkt hoeveelheden. De hoogte van elke staaf geeft de waarde aan.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Aflezen',
        content:
          'Stap 1: Lees de titel\nStap 2: Kijk welke categorie bij elke staaf hoort (x-as)\nStap 3: Lees de hoogte af op de y-as\nStap 4: Vergelijk de staven',
      },
      {
        title: 'Vergelijken',
        content:
          'Hoogste staaf = meeste\nLaagste staaf = minste\nVerschil = hoogte aftrekken',
      },
    ],
  },
  'STU-LIJN': {
    familyId: 'STU-LIJN',
    defaultExplanation:
      'Een lijndiagram toont veranderingen over tijd. De lijn gaat omhoog (stijging), omlaag (daling) of blijft gelijk.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Trends herkennen',
        content:
          'Lijn omhoog → stijging/toename\nLijn omlaag → daling/afname\nLijn vlak → gelijk/stabiel\nSteile lijn → snelle verandering',
      },
      {
        title: 'Aflezen',
        content:
          'Zoek het punt op de x-as (tijd)\nGa recht omhoog tot de lijn\nGa dan naar links naar de y-as\nLees de waarde af',
      },
    ],
  },
  'STU-CIRKEL': {
    familyId: 'STU-CIRKEL',
    defaultExplanation:
      'Een cirkeldiagram toont de verdeling van een geheel. Alle delen samen zijn altijd 100%.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Aflezen',
        content:
          'Het grootste deel = het grootste percentage\nAlle delen samen = 100%\nDe helft van de cirkel = 50%\nEen kwart = 25%',
      },
      {
        title: 'Vergelijken',
        content:
          'Vergelijk de grootte van de taartpunten.\nGroter stuk = groter percentage.\nLees de legenda om te weten wat elke kleur betekent.',
      },
    ],
  },
  'STU-TIJDLIJN': {
    familyId: 'STU-TIJDLIJN',
    defaultExplanation:
      'Een tijdlijn toont gebeurtenissen in chronologische volgorde. Links/boven = vroeger, rechts/onder = later.',
    requiresHints: false,
    requiresVisual: false,
    hints: [
      {
        title: 'Tijdlijn lezen',
        content:
          'Stap 1: Kijk naar het begin en eind (welke periode?)\nStap 2: Let op de schaal (jaren, eeuwen?)\nStap 3: Lees de gebeurtenissen van links naar rechts\nStap 4: Let op de afstand tussen gebeurtenissen',
      },
    ],
  },
  'STU-FLOWCHART': {
    familyId: 'STU-FLOWCHART',
    defaultExplanation:
      'Een flowchart toont stappen in een proces. Volg de pijlen en beantwoord ja/nee-vragen om het juiste pad te volgen.',
    requiresHints: false,
    requiresVisual: false,
    hints: [
      {
        title: 'Flowchart lezen',
        content:
          'Start bij "Begin"\nRechthoek = actie/stap\nRuit = vraag (ja/nee)\nVolg de pijlen\nStop bij "Einde"',
      },
      {
        title: 'Tip',
        content:
          'Lees elke vraag zorgvuldig.\nVolg altijd de pijl die bij je antwoord hoort.\nSla geen stappen over!',
      },
    ],
  },
  'STU-ROUTE': {
    familyId: 'STU-ROUTE',
    defaultExplanation:
      'Bij route plannen gebruik je een kaart, de schaal en windrichtingen om de weg te vinden.',
    requiresHints: false,
    requiresVisual: false,
    hints: [
      {
        title: 'Route plannen',
        content:
          'Stap 1: Zoek het beginpunt en eindpunt\nStap 2: Zoek de kortste/snelste weg\nStap 3: Let op de schaal voor afstanden\nStap 4: Gebruik windrichtingen (N/Z/O/W)',
      },
    ],
  },
  'STU-NOTITIES': {
    familyId: 'STU-NOTITIES',
    defaultExplanation:
      'Goede notities bevatten kernwoorden, geen hele zinnen. Gebruik kopjes, streepjes en afkortingen.',
    requiresHints: false,
    requiresVisual: false,
    hints: [
      {
        title: 'Tips voor notities',
        content:
          'Gebruik kernwoorden (niet hele zinnen)\nMaak kopjes per onderwerp\nGebruik streepjes (-) voor opsommingen\nOnderstreep belangrijke woorden\nSchrijf afkortingen (bijv., bv., evt.)',
      },
    ],
  },
  'STU-SAMENVATTEN': {
    familyId: 'STU-SAMENVATTEN',
    defaultExplanation:
      'Bij samenvatten zoek je de hoofdgedachte en belangrijkste punten. Laat details en voorbeelden weg.',
    requiresHints: false,
    requiresVisual: false,
    hints: [
      {
        title: 'Stappenplan samenvatten',
        content:
          'Stap 1: Lees de tekst helemaal\nStap 2: Zoek de hoofdgedachte (waar gaat het over?)\nStap 3: Zoek de belangrijkste punten per alinea\nStap 4: Schrijf in eigen woorden\nStap 5: Laat details en voorbeelden weg',
      },
    ],
  },
  'STU-MINDMAP': {
    familyId: 'STU-MINDMAP',
    defaultExplanation:
      'Een mindmap is een visueel schema met het hoofdonderwerp in het midden en vertakkingen naar deelonderwerpen.',
    requiresHints: false,
    requiresVisual: false,
    hints: [
      {
        title: 'Mindmap maken',
        content:
          'Stap 1: Schrijf het hoofdonderwerp in het midden\nStap 2: Trek takken naar deelonderwerpen\nStap 3: Voeg details toe aan de takken\nStap 4: Gebruik kleuren en tekeningen',
      },
    ],
  },
  'STU-PLANNEN': {
    familyId: 'STU-PLANNEN',
    defaultExplanation:
      'Bij plannen verdeel je een grote taak in kleine stappen en bepaal je wanneer je wat doet.',
    requiresHints: false,
    requiresVisual: false,
    hints: [
      {
        title: 'Stappenplan plannen',
        content:
          'Stap 1: Wat moet er af? (eindresultaat)\nStap 2: Wanneer moet het af? (deadline)\nStap 3: Verdeel in kleine taken\nStap 4: Plan elke taak in je agenda\nStap 5: Begin met de moeilijkste taak',
      },
    ],
  },
}

/**
 * Module Statistics
 * Auto-calculated from support entries
 */
export const STU_SUPPORT_STATS = {
  moduleCode: 'STU',
  moduleName: 'Studievaardigheden',
  totalFamilies: Object.keys(STU_SUPPORT).length,
  withHints: Object.values(STU_SUPPORT).filter((e) => e.hints?.length).length,
  withVisuals: Object.values(STU_SUPPORT).filter((e) => e.defaultVisual).length,
  withExplanations: Object.values(STU_SUPPORT).filter(
    (e) => e.defaultExplanation,
  ).length,
  requiresHintsCount: Object.values(STU_SUPPORT).filter((e) => e.requiresHints)
    .length,
  requiresVisualCount: Object.values(STU_SUPPORT).filter(
    (e) => e.requiresVisual,
  ).length,
}
